/**
 * Admin Responsibilities service, backed by Firestore. Writes are admin-only
 * via Firestore rules.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../../../../shared/firebase';

const now = () => new Date().toISOString();

const withCount = (resp) => ({
  ...resp,
  frameworkCount: Array.isArray(resp.frameworks) ? resp.frameworks.length : 0,
});

/**
 * Get all responsibilities (with optional filters)
 * @returns {Promise<Array>}
 */
export const getAllResponsibilities = async (filters = {}) => {
  try {
    let q = collection(db, 'responsibilities');
    const clauses = [];
    if (filters.roleId) clauses.push(where('roleId', '==', filters.roleId));
    if (filters.isUniversal !== undefined) {
      clauses.push(where('isUniversal', '==', !!filters.isUniversal));
    }
    if (clauses.length) q = query(q, ...clauses);
    const snap = await getDocs(q);
    const results = [];
    snap.forEach((d) => results.push(withCount(d.data())));
    return results;
  } catch (error) {
    console.error('Error fetching responsibilities:', error);
    throw error;
  }
};

/**
 * Get responsibility by ID
 * @returns {Promise<Object>}
 */
export const getResponsibilityById = async (responsibilityId) => {
  try {
    const snap = await getDoc(doc(db, 'responsibilities', responsibilityId));
    if (!snap.exists()) {
      throw new Error('Responsibility not found');
    }
    return withCount(snap.data());
  } catch (error) {
    console.error('Error fetching responsibility:', error);
    throw error;
  }
};

/**
 * Create new responsibility
 * @returns {Promise<Object>}
 */
export const createResponsibility = async (data) => {
  try {
    const responsibilityId = uuidv4();
    const responsibility = {
      responsibilityId,
      responsibilityName: data.responsibilityName,
      definition: data.definition || null,
      isUniversal: data.isUniversal || false,
      roleId: data.roleId || null,
      frameworks: [],
      createdAt: now(),
      updatedAt: now(),
    };
    await setDoc(doc(db, 'responsibilities', responsibilityId), responsibility);
    return responsibility;
  } catch (error) {
    console.error('Error creating responsibility:', error);
    throw error;
  }
};

/**
 * Update responsibility
 * @returns {Promise<Object>}
 */
export const updateResponsibility = async (responsibilityId, updates) => {
  try {
    const ref = doc(db, 'responsibilities', responsibilityId);
    const fields = { updatedAt: now() };
    if (updates.responsibilityName !== undefined) fields.responsibilityName = updates.responsibilityName;
    if (updates.definition !== undefined) fields.definition = updates.definition;
    if (updates.isUniversal !== undefined) fields.isUniversal = updates.isUniversal;
    if (updates.roleId !== undefined) fields.roleId = updates.roleId;
    await updateDoc(ref, fields);
    const snap = await getDoc(ref);
    return withCount(snap.data());
  } catch (error) {
    console.error('Error updating responsibility:', error);
    throw error;
  }
};

/**
 * Delete responsibility
 * @returns {Promise<Object>}
 */
export const deleteResponsibility = async (responsibilityId) => {
  try {
    await deleteDoc(doc(db, 'responsibilities', responsibilityId));
    return { success: true, message: 'Responsibility deleted successfully' };
  } catch (error) {
    console.error('Error deleting responsibility:', error);
    throw error;
  }
};
